<script setup lang="ts">
import { KsButton } from "@aschehoug/kloss";
import { faArrowDownLong, faArrowUpLong } from "@fortawesome/pro-regular-svg-icons";

const props = defineProps<{
  variants: PacketVariant[];
  related: Record<string, PacketVariant>;
}>();

const packet = inject(keys.currentPacket);

const auth = useAuthorizeStore();

const open = ref(false);

const cheapestVariant = usePrices(props.variants);

// With more elements, we want a longer transition, since the list will be taller. We never want it to be shorter than 300ms.
const transitionDuration = computed(() => {
  const base = 300;
  const value = Math.max(base, 0.5 * base * (props.variants.length / 3));
  return `${value}ms`;
});
</script>

<template>
  <PacketVariantCardContainer
    v-if="variants.length > 0"
    :variants="variants"
    :related="related"
    class="@container"
  >
    <PacketVariantCardDiscountIcon />
    <header class="flex flex-col justify-between gap-[40px] px-s pb-l pt-m @md:px-m @md:py-l @2xl:flex @2xl:flex-row @2xl:items-center @2xl:gap-l @2xl:p-l">
      <div v-if="packet?.independentVariantsMeta" class="flex flex-col gap-m text-black-80">
        <h3
          v-if="packet.independentVariantsMeta.title"
          class="text-2xl font-semibold md:text-3xl"
          v-text="packet.independentVariantsMeta.title"
        />
        <div
          v-if="packet.independentVariantsMeta.text"
          class="text-base md:text-xl"
          v-html="packet.independentVariantsMeta.text"
        />
      </div>

      <div v-if="cheapestVariant" class="flex flex-col gap-s bg-gray-10 p-s">
        <template v-if="cheapestVariant !== 'NOT_APPLICABLE'">
          <div class="text-lg font-semibold text-blue-50 md:text-xl">
            <span>Fra&nbsp;</span>
            <template v-if="cheapestVariant.discounted">
              <del
                aria-label="Ordinær pris per tilgang"
                v-text="
                  norwegianPrice(
                    auth.isConsumer ? cheapestVariant.original.incVat : cheapestVariant.original.exVat,
                  )
                "
              />
              &nbsp;
              <ins
                aria-label="Rabattert pris per tilgang"
                class="text-red-30 no-underline"
                v-text="
                  norwegianPrice(
                    auth.isConsumer
                      ? cheapestVariant.discounted.incVat
                      : cheapestVariant.discounted.exVat,
                  )
                "
              />
            </template>
            <span v-else>
              {{ norwegianPrice(
                auth.isConsumer ? cheapestVariant.actual.incVat : cheapestVariant.actual.exVat,
              ) }}&nbsp;per tilgang
            </span>
            <!-- FIXME: Only show if incVat and exVat are different -->
            <span class="mt-2xs block text-sm font-normal">
              {{ norwegianPrice(cheapestVariant.actual.incVat) }} inkl. mva.
            </span>
          </div>
          <PriceLoginCta class="max-w-sm text-base font-normal text-gray-50" />
        </template>
        <p v-else class="max-w-sm text-base font-normal text-gray-50">
          Selges kun til bedrifter. Logg inn med Feide for å bestille.
        </p>
      </div>
    </header>

    <ul
      data-variants
      class="grid gap-m overflow-hidden px-s pb-m @md:px-m @md:pb-l @2xl:px-l @2xl:pb-xl"
      :class="open ? 'h-auto' : 'h-20'"
    >
      <li
        v-for="variant in variants"
        :key="variant.sku"
      >
        <PacketPickAndMatchCard :variant="variant" class="h-full" />
      </li>
    </ul>

    <div class="border-t border-gray-10 px-s py-m text-center @md:px-m @md:py-l @2xl:p-l">
      <KsButton
        variant="primary"
        shape="rounded"
        size="large"
        :icon-left="open ? faArrowUpLong : faArrowDownLong"
        class="[--ks-primary:theme(colors.blue.50)] [--ks-primarytext:theme(colors.white)] [--ks-secondary:theme(colors.blue.40)]"
        @click="open = !open"
      >
        {{ open ? 'Lukk' : 'Se fagene og kjøp her' }}
      </KsButton>
    </div>
  </PacketVariantCardContainer>
</template>

<style scoped>
[data-variants] {
  grid-template-columns: repeat(auto-fit, minmax(min(333px, 100%), 1fr));
  transition: height v-bind(transitionDuration) ease-in-out;
}
</style>
